import { submissionApiClient } from "@/api/submission-api";
import { profileApi } from "@/api/profile-api";
import { workspaceApi } from "@/api/workspace-api";
import ApplicationStep from "@/components/portal/steps/application-step";
import BasicInfoStep from "@/components/portal/steps/basic-info-step";
import CompanyInfoStep from "@/components/portal/steps/company-info-step";
import PortcoUpdateStep from "@/components/portal/steps/portco-update-step";
import UploadDocumentsStep from "@/components/portal/steps/upload-documents-step";
import SubmissionHeader from "@/components/portal/submission-header";
import { useAuthStore } from "@/store/auth-store";
import { IWorkspace } from "@/types/data-models";
import { ArrowLeft, CircleAlert, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StepColumn } from "@/components/portal/portal-ui";
import { formatPortalDate } from "@/components/portal/portal-utils";
import { StepCoverContext } from "@/components/portal/step-cover-context";
import {
  fundCover,
  PublicCover,
  PublicState,
  RaiseBoardNav,
  RaiseBoardShell,
} from "@/pages/raise-board/rb-ui";
import { useUser } from "@clerk/clerk-react";
import { setPostHogGroup } from "@/lib/posthog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router";

export default function SubmissionPage() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug: string }>();
  const { isInitialized, isLoading, checkAuth } = useAuthStore();
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
  const navigate = useNavigate();
  const opportunityId = searchParams.get("id");
  const hasCalledMeApi = useRef(false);

  // Convert Clerk user to the format expected by the rest of the app
  const user = useMemo(() => {
    if (!clerkUser) return null;
    return {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || "",
      firstName: clerkUser.firstName || "",
      lastName: clerkUser.lastName || "",
    };
  }, [
    clerkUser?.id,
    clerkUser?.primaryEmailAddress?.emailAddress,
    clerkUser?.firstName,
    clerkUser?.lastName,
  ]);

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opportunityData, setOpportunityData] = useState<IWorkspace | null>(
    null
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    companyName: "",
    foundedMonth: new Date().getMonth() + 1,
    foundedYear: new Date().getFullYear(),
  });

  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [, setApplicationStatus] = useState<{
    hasApplied: boolean;
    applicationStatus: string | null;
    applicationId: string | null;
  } | null>(null);
  const [existingApplicationData, setExistingApplicationData] =
    useState<any>(null);
  const [isPortfolioCompany, setIsPortfolioCompany] = useState<boolean | null>(
    null
  );
  const [hasInitializedUserData, setHasInitializedUserData] = useState(false);
  const [isInitializingUserData, setIsInitializingUserData] = useState(false);

  useEffect(() => {
    if (!isInitialized && !isLoading) {
      checkAuth().catch((err) => {
        console.error("Error initializing auth state:", err);
      });
    }
  }, [checkAuth, isInitialized, isLoading]);

  // Call /me API on page load to get user and company info early
  useEffect(() => {
    const fetchUserProfile = async () => {
      // Prevent multiple calls
      if (hasCalledMeApi.current) {
        console.log("⏭️ /me API already called, skipping...");
        return;
      }

      console.log("🔍 fetchUserProfile called", {
        isClerkLoaded,
        hasUser: !!user,
        userId: user?.id,
        hasCompanyInfo: !!companyInfo,
      });

      // Wait for Clerk to be loaded
      if (!isClerkLoaded) {
        console.log("⏳ Waiting for Clerk to load...");
        return;
      }

      // Only fetch if user is logged in and we don't already have company info
      if (user && user.id && !companyInfo) {
        hasCalledMeApi.current = true;
        console.log("✅ Calling /me API...");
        try {
          const profileResponse = await profileApi.getMe();
          console.log("📥 Profile response on page load:", profileResponse);

          if (profileResponse.success && profileResponse.company) {
            // Convert the company profile to the format expected by the rest of the app
            const companyData = {
              _id: profileResponse.company.id,
              company_name: profileResponse.company.companyName,
              short_bio: profileResponse.company.shortBio,
              detailed_description: profileResponse.company.detailedDescription,
              company_website: profileResponse.company.website,
              industry: profileResponse.company.industry,
              company_type: profileResponse.company.companyType,
              stage: profileResponse.company.stage,
              city: profileResponse.company.city,
              province: profileResponse.company.province,
              country: profileResponse.company.country,
              address: profileResponse.company.address,
              contact_phone: profileResponse.company.contactPhone,
              business_model: profileResponse.company.businessModel,
              // GICS hierarchy (2-level)
              gics_industry: profileResponse.company.gics_industry,
              gics_sub_industry: profileResponse.company.gics_sub_industry,
              // Revenue model
              revenue_model: profileResponse.company.revenue_model,
              facebook: profileResponse.company.facebook,
              twitter: profileResponse.company.twitter,
              linkedIn: profileResponse.company.linkedIn,
              instagram: profileResponse.company.instagram,
            };

            console.log(
              "✅ Company data from profile (page load):",
              companyData
            );
            setCompanyInfo(companyData);
          } else {
            console.log("ℹ️ User does not have a company (page load)");
          }
        } catch (err) {
          console.error("❌ Error fetching user profile on page load:", err);
          hasCalledMeApi.current = false; // Reset on error so we can retry
          // Don't fail the whole page if this fails
        }
      } else {
        if (companyInfo) {
          console.log("ℹ️ Already have company info, skipping /me API call");
        } else {
          console.log("ℹ️ No user found, skipping /me API call");
        }
      }
    };

    fetchUserProfile();
  }, [isClerkLoaded, user?.id, companyInfo]);

  useEffect(() => {
    const initializeSubmission = async () => {
      if (!opportunityId && !slug) {
        setError("No opportunity ID or slug provided");
        setLoading(false);
        return;
      }

      // Wait for auth store to be initialized
      if (!isInitialized || isLoading) {
        return;
      }

      // Reset per-opportunity state so stale values don't persist across slug/id changes
      setLoading(true);
      setExistingApplicationData(null);
      setIsPortfolioCompany(false);
      setHasInitializedUserData(false);

      try {
        // Fetch opportunity data by slug or ID
        const response = slug
          ? await workspaceApi.getPortalWorkspaceBySlug(slug)
          : await workspaceApi.getPortalWorkspace(opportunityId!);
        console.log("Fetched opportunity data:", response.result);
        setOpportunityData(response.result);
        const resolvedOpportunityId = response.result._id;

        // Link subsequent portal events to the workspace group so PostHog
        // records the person↔workspace relationship for founders visiting
        // the portal (they never hit the main-app workspace store).
        if (resolvedOpportunityId) {
          setPostHogGroup("workspace", resolvedOpportunityId, {
            name: response.result.opportunity_name,
          });
        }

        // If accessed via legacy ID route and workspace has a slug, redirect to slug URL
        if (opportunityId && !slug && response.result.portal_slug) {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.delete('id');
          const queryString = currentParams.toString();
          const redirectUrl = `/p/${response.result.portal_slug}/submission${queryString ? `?${queryString}` : ''}`;
          navigate(redirectUrl, { replace: true });
          return;
        }

        console.log("Checking user", user);

        // NOTE: do NOT gate this on `hasInitializedUserData`. The closure
        // captures a stale value, and the state is reset to `false` a few
        // lines above on every effect run, which previously combined with
        // `hasInitializedUserData` being in the deps array to create an
        // infinite re-fetch / re-render loop on this page (SAAS-601).
        // Re-runs are now controlled solely by the deps array — slug,
        // opportunityId, auth state, and user identity.
        if (user) {
          let isPortco = false;
          let hasCompanyData = false;
          let applicationStatusResult = null;

          console.log("User is found", user);
          setIsInitializingUserData(true);

          // Check application status
          try {
            const statusResponse =
              await submissionApiClient.application.checkApplicationStatus(
                resolvedOpportunityId,
                user.id
              );

            applicationStatusResult = statusResponse;

            setApplicationStatus({
              hasApplied: statusResponse.hasApplied,
              applicationStatus: statusResponse.applicationStatus,
              applicationId: statusResponse.applicationId,
            });

            // Check if this is a portfolio company
            isPortco =
              statusResponse.applicationStatus === "Portfolio Companies";
            setIsPortfolioCompany(isPortco);

            // If application exists, we'll fetch the data after we have company info
            if (statusResponse.hasApplied) {
              console.log(
                "✅ Application exists, will fetch data after company info is loaded"
              );
            } else {
            }
          } catch (err) {
            console.error("Error checking application status:", err);
            // Don't fail the whole page if this check fails
            setApplicationStatus({
              hasApplied: false,
              applicationStatus: null,
              applicationId: null,
            });
            setIsPortfolioCompany(false);
          }

          // Check if user has company data using the /me endpoint
          try {
            const profileResponse = await profileApi.getMe();
            console.log("Profile response:", profileResponse);

            if (profileResponse.success && profileResponse.company) {
              // Convert the company profile to the format expected by the rest of the app
              const companyData = {
                _id: profileResponse.company.id,
                company_name: profileResponse.company.companyName,
                short_bio: profileResponse.company.shortBio,
                detailed_description:
                  profileResponse.company.detailedDescription,
                company_website: profileResponse.company.website,
                industry: profileResponse.company.industry,
                company_type: profileResponse.company.companyType,
                stage: profileResponse.company.stage,
                city: profileResponse.company.city,
                province: profileResponse.company.province,
                country: profileResponse.company.country,
                address: profileResponse.company.address,
                contact_phone: profileResponse.company.contactPhone,
                business_model: profileResponse.company.businessModel,
                // GICS hierarchy (2-level)
                gics_industry: profileResponse.company.gics_industry,
                gics_sub_industry: profileResponse.company.gics_sub_industry,
                // Revenue model
                revenue_model: profileResponse.company.revenue_model,
                facebook: profileResponse.company.facebook,
                twitter: profileResponse.company.twitter,
                linkedIn: profileResponse.company.linkedIn,
                instagram: profileResponse.company.instagram,
              };

              console.log("Company data from profile:", companyData);
              setCompanyInfo(companyData);
              hasCompanyData = true;

              // If application exists, fetch the application data now that we have company info
              // We need to check the applicationStatusResult from the outer scope, not the state
              if (applicationStatusResult?.hasApplied) {
                console.log(
                  "🔄 Fetching application data for company:",
                  companyData._id
                );
                try {
                  const applicationDataResponse =
                    await submissionApiClient.application.getApplicationData(
                      companyData._id,
                      resolvedOpportunityId
                    );
                  console.log(
                    "📥 Raw application data response:",
                    applicationDataResponse
                  );
                  setExistingApplicationData(applicationDataResponse.result);
                  console.log(
                    "✅ Successfully set existing application data:",
                    applicationDataResponse.result
                  );
                } catch (err) {
                  console.error(
                    "❌ Error fetching existing application data:",
                    err
                  );
                  // Don't fail the whole flow if this fails
                }
              }
            } else {
              console.log("User does not have a company yet");
            }
          } catch (err) {
            console.error("Error fetching user profile:", err);
            // User doesn't have company data, will start from step 1
          }

          // Set the appropriate step based on portfolio company status and company data
          console.log(
            "Step determination - hasCompanyData:",
            hasCompanyData,
            "isPortco:",
            isPortco
          );
          if (hasCompanyData) {
            if (isPortco) {
              setCurrentStep(4); // Portfolio companies go directly to step 4 (portco update)
            } else {
              setCurrentStep(2); // Regular companies go to step 2 (upload documents)
            }
          } else {
            setCurrentStep(1); // No company data, start from step 1
          }

          setHasInitializedUserData(true);
          setIsInitializingUserData(false);
        } else if (!user) {
          // No user, start from step 1
          setCurrentStep(1);
        }
      } catch (err) {
        console.error("Error initializing submission:", err);
        setError("Failed to load opportunity data");
      } finally {
        setLoading(false);
      }
    };

    initializeSubmission();
    // `hasInitializedUserData` is intentionally NOT in this deps array — it
    // is written inside this effect, so including it would cause an infinite
    // loop. See the comment in the `if (user)` block above.
  }, [opportunityId, slug, isInitialized, isLoading, user?.id]);

  // Redirect to step 1 if we're on step 3 but don't have company info after initialization
  useEffect(() => {
    if (
      currentStep === 2 &&
      !companyInfo &&
      !isInitializingUserData &&
      hasInitializedUserData &&
      !isPortfolioCompany
    ) {
      console.log("No company info found, redirecting to step 1");
      setCurrentStep(1);
    }
  }, [
    currentStep,
    companyInfo,
    isInitializingUserData,
    hasInitializedUserData,
    isPortfolioCompany,
  ]);

  const nextStep = useCallback(() => {
    // For portfolio companies, we have 2 steps total (1 -> 4)
    // For regular companies, we have 4 steps total (1 -> 2 -> 3 -> 4)
    if (isPortfolioCompany) {
      // Portfolio companies: step 1 -> step 4
      if (currentStep === 1) {
        setCurrentStep(4);
      }
    } else {
      // Regular companies: step 1 -> 2 -> 3 -> 4
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  }, [currentStep, isPortfolioCompany]);

  const prevStep = useCallback(() => {
    if (isPortfolioCompany) {
      // Portfolio companies: step 1 (auth) -> step 4
      // Can't go back from step 4 — step 1 is auth gate, steps 2-3 were skipped
      return;
    } else {
      // Regular companies: step 4 -> 3 -> 2 (step 1 is auth gate, never go back)
      if (currentStep > 2) {
        setCurrentStep(currentStep - 1);
      }
    }
  }, [currentStep, isPortfolioCompany]);

  // Track which steps have been completed for clickable step navigation
  useEffect(() => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (isPortfolioCompany) {
        // Portfolio flow: step 1 is auth gate (not navigable), steps 2-3 skipped
        // No steps are clickable for portfolio companies
      } else {
        // Regular flow: steps 2+ before current are completed
        // Step 1 is auth gate — once past it, never go back
        for (let s = 2; s < currentStep; s++) {
          next.add(s);
        }
      }
      return next;
    });
  }, [currentStep, isPortfolioCompany]);

  const goToStep = useCallback(
    (step: number) => {
      // Only allow navigating to completed steps that are before the current step
      if (step >= 1 && step < currentStep && completedSteps.has(step)) {
        setCurrentStep(step);
      }
    },
    [currentStep, completedSteps]
  );


  const handleCompanyInfoUpdate = useCallback(
    (updatedCompanyInfo: any) => {
      console.log("handleCompanyInfoUpdate called with:", updatedCompanyInfo);
      console.log("Current companyInfo before update:", companyInfo);

      // Map form field names (camelCase) back to API field names (snake_case)
      // This ensures data persists correctly when navigating back to step 2
      const mappedInfo = {
        company_website: updatedCompanyInfo.companyWebsite,
        linkedIn: updatedCompanyInfo.linkedInUrl,
        address: updatedCompanyInfo.address,
        country: updatedCompanyInfo.country,
        // GICS hierarchy (2-level)
        gics_industry: updatedCompanyInfo.gicsIndustry,
        gics_sub_industry: updatedCompanyInfo.gicsSubIndustry,
        // Business & Revenue (multi-select — arrays of short codes)
        business_model: updatedCompanyInfo.businessModel,
        revenue_model: updatedCompanyInfo.revenueModel,
        stage: updatedCompanyInfo.companyStage,
        contact_phone: updatedCompanyInfo.contactPhone,
      };

      // Merge the updated info with existing company data to preserve _id
      setCompanyInfo((prevCompanyInfo: any) => {
        const mergedInfo = {
          ...prevCompanyInfo,
          ...mappedInfo,
        };
        console.log("Merged company info:", mergedInfo);
        return mergedInfo;
      });
      nextStep();
    },
    [nextStep, companyInfo]
  );

  const getCompletionUrl = () =>
    slug
      ? `/p/${slug}`
      : opportunityData?.portal_slug
        ? `/p/${opportunityData.portal_slug}`
        : `/portal?id=${opportunityId}`;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            formData={formData}
            setFormData={setFormData}
            nextStep={nextStep}
            setCompanyInfo={setCompanyInfo}
          />
        );
      case 2:
        // Only show for non-portfolio companies
        if (isPortfolioCompany) {
          return null;
        }
        // If we don't have company info and we're done initializing, redirect to step 1
        if (!companyInfo && !isInitializingUserData && hasInitializedUserData) {
          // Use useEffect to redirect (handled below)
          return (
            <StepColumn>
              <PublicState icon={<Building2 />} title="No company yet" className="py-6">
                Go back to the first step and add your company to continue with
                this application.
              </PublicState>
            </StepColumn>
          );
        }
        // Show loading only if we're still initializing
        if (!companyInfo) {
          return <StepSkeleton label="Loading your company" />;
        }
        return (
          <CompanyInfoStep
            companyData={companyInfo}
            onNext={handleCompanyInfoUpdate}
          />
        );
      case 3:
        // Only show for non-portfolio companies
        if (isPortfolioCompany) {
          return null;
        }
        return (
          <UploadDocumentsStep
            companyData={companyInfo}
            opportunityId={opportunityData!._id}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 4:
        // Check if user is a portfolio company
        if (isPortfolioCompany && opportunityData && companyInfo) {
          return (
            <PortcoUpdateStep
              opportunityData={opportunityData}
              companyData={companyInfo}
              onComplete={() => navigate(getCompletionUrl())}
            />
          );
        }

        // Regular application step for non-portfolio companies
        console.log(
          "🎯 Rendering ApplicationStep with existingApplicationData:",
          existingApplicationData
        );
        return opportunityData && companyInfo ? (
          <ApplicationStep
            opportunityData={opportunityData}
            companyData={companyInfo}
            existingApplicationData={existingApplicationData}
            onBack={prevStep}
            onComplete={() => navigate(getCompletionUrl())}
          />
        ) : (
          <StepSkeleton label="Loading the application form" />
        );
      default:
        return null;
    }
  };

  // Show loading while auth is initializing or while loading submission data
  if (!isInitialized || isLoading || loading || isInitializingUserData) {
    return (
      <RaiseBoardShell>
        <div className="ink-scope bg-background">
          <div className="h-14 border-b border-border/60" />
          <div className="mx-auto w-full max-w-2xl px-5 pb-12 pt-10 sm:px-8 sm:pb-14 sm:pt-14">
            <Skeleton className="h-3.5 w-36" />
            <Skeleton className="mt-6 h-11 w-64 rounded-lg" />
            <Skeleton className="mt-4 h-4 w-80 max-w-full" />
          </div>
        </div>
        <div className="pt-12">
          <StepSkeleton
            label={
              !isInitialized || isLoading
                ? "Getting things ready"
                : isInitializingUserData
                  ? "Loading your details"
                  : "Loading the application"
            }
          />
        </div>
      </RaiseBoardShell>
    );
  }

  if (error || !opportunityData) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={() => navigate("/sign-in")} />
        <PublicCover
          size="lg"
          eyebrow={
            <>
              <CircleAlert />
              Application
            </>
          }
          title="We couldn't open this application"
          lede={`${error || "This portal couldn't be found."} If it keeps happening, check the link with the fund that shared it.`}
          aside={
            <Button
              type="button"
              className="h-11 rounded-lg px-5"
              onClick={() => navigate(-1)}
            >
              Go back
            </Button>
          }
        />
      </RaiseBoardShell>
    );
  }

  const fundName = opportunityData.opportunity_name || "this fund";
  const fundLogo =
    opportunityData.affiliated_company?.files?.find(
      (file) => file.file_type === "company avatar"
    )?.file_url ?? null;
  const closes = formatPortalDate(opportunityData.end_intake_date);
  const stepCover = {
    background: fundCover(opportunityData),
    eyebrow: (
      <span>
        Step {currentStep} of 4
        {closes ? <span className="text-muted-foreground/70"> · Closes {closes}</span> : null}
      </span>
    ),
    aside:
      currentStep > 2 ? (
        <button
          type="button"
          onClick={prevStep}
          className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors duration-150 hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
      ) : undefined,
  };
  const stepLabels = [
    "About you",
    "Company",
    "Documents",
    isPortfolioCompany ? "Update" : "Application",
  ];

  return (
    <RaiseBoardShell footerNote={`Applying to ${fundName}. Your progress is saved as you go.`}>
      <SubmissionHeader
        activeStep={currentStep}
        completedSteps={completedSteps}
        onGoToStep={goToStep}
        fundName={opportunityData.opportunity_name}
        logoUrl={fundLogo}
        homeHref={getCompletionUrl()}
        labels={stepLabels}
        background={fundCover(opportunityData)}
      />

      {/* Every step opens on an ink band (StepCardHeader) that bleeds to the
          edges; clip sideways overflow so a classic scrollbar can't add a
          horizontal scroll. */}
      <StepCoverContext.Provider value={stepCover}>
        <div className="overflow-x-clip pb-8">{renderStep()}</div>
      </StepCoverContext.Provider>
    </RaiseBoardShell>
  );
}

/** A step's shape while it loads: title, a line of context, a few fields. */
function StepSkeleton({ label }: { label: string }) {
  return (
    <StepColumn>
      <div role="status" aria-label={label}>
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="mt-5 h-8 w-56" />
        <Skeleton className="mt-3 h-4 w-80 max-w-full" />
        <div className="mt-10 space-y-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    </StepColumn>
  );
}
